import { CartUpdateEvent } from '@theme/events';

const CAROUSEL_SELECTOR = '[data-cart-rec-carousel]';
const TRACK_SELECTOR = '[data-cart-rec-track]';
const PREV_SELECTOR = '[data-cart-rec-prev]';
const NEXT_SELECTOR = '[data-cart-rec-next]';
const AUTO_CARDS_SELECTOR = '[data-cart-auto-rec-cards]';
const AUTO_SECTION_ID = 'cart-auto-recommendation-cards';
const INITIALIZED_TRACKS = new WeakSet();

function updateArrows(track, prevBtn, nextBtn) {
  const maxScroll = track.scrollWidth - track.clientWidth;
  const cards = track.querySelectorAll('.cart-rec-card');
  const firstCard = cards[0];
  const styles = getComputedStyle(track);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;
  const cardWidth = firstCard?.getBoundingClientRect().width || 0;
  const visibleCardCount =
    cardWidth > 0 && track.clientWidth > 0 ? Math.max(1, Math.floor((track.clientWidth + gap) / (cardWidth + gap))) : 4;
  const hasOverflow = maxScroll > 1 || cards.length > visibleCardCount;

  prevBtn.hidden = !hasOverflow;
  nextBtn.hidden = !hasOverflow;
  prevBtn.disabled = track.scrollLeft <= 1;
  nextBtn.disabled = maxScroll > 1 && track.scrollLeft >= maxScroll - 1;
}

function scrollCarousel(track, direction) {
  const card = track.querySelector('.cart-rec-card');
  const styles = getComputedStyle(track);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;
  const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth;

  track.scrollBy({ left: direction * distance, behavior: 'smooth' });
}

function initCartRecommendations(root = document) {
  if (root instanceof HTMLElement && root.matches(CAROUSEL_SELECTOR)) {
    initCarousel(root);
  }

  const carousels = root.querySelectorAll?.(CAROUSEL_SELECTOR) || [];

  for (const wrapper of carousels) {
    initCarousel(wrapper);
  }
}

function initCarousel(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;

  const track = wrapper.querySelector(TRACK_SELECTOR);
  const prevBtn = wrapper.querySelector(PREV_SELECTOR);
  const nextBtn = wrapper.querySelector(NEXT_SELECTOR);

  if (
    !(track instanceof HTMLElement) ||
    !(prevBtn instanceof HTMLButtonElement) ||
    !(nextBtn instanceof HTMLButtonElement)
  ) {
    return;
  }
  if (INITIALIZED_TRACKS.has(track)) return;

  INITIALIZED_TRACKS.add(track);

  const refresh = () => updateArrows(track, prevBtn, nextBtn);
  const cartDrawer = wrapper.closest('cart-drawer-component');
  const resizeObserver = new ResizeObserver(refresh);
  const mutationObserver = new MutationObserver(refresh);

  track.addEventListener('scroll', refresh, { passive: true });
  window.addEventListener('resize', refresh);
  cartDrawer?.addEventListener('dialog:open', () => {
    loadAutoRecommendations(wrapper);
    requestAnimationFrame(refresh);
    setTimeout(refresh, 150);
  });
  resizeObserver.observe(track);
  resizeObserver.observe(wrapper);
  mutationObserver.observe(track, { childList: true });

  for (const image of track.querySelectorAll('img')) {
    if (!image.complete) image.addEventListener('load', refresh, { once: true });
  }

  refresh();
  requestAnimationFrame(refresh);
  setTimeout(refresh, 150);
  setTimeout(refresh, 300);
}

async function loadAutoRecommendations(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;

  const track = wrapper.querySelector(TRACK_SELECTOR);
  const url = wrapper.dataset.recommendationsUrl;
  const productIds = (wrapper.dataset.autoProductIds || '').split(',').filter(Boolean);
  if (!(track instanceof HTMLElement) || !url || productIds.length === 0) return;

  const requestKey = `${productIds.join(',')}|${wrapper.dataset.cartProductIds || ''}`;
  if (wrapper.dataset.autoRecommendationsLoading === requestKey) return;
  if (wrapper.dataset.autoRecommendationsLoaded === requestKey) return;

  wrapper.dataset.autoRecommendationsLoading = requestKey;
  const existingProductIds = new Set((wrapper.dataset.cartProductIds || '').split('#').filter(Boolean));
  for (const card of track.querySelectorAll('[data-cart-rec-product-id]')) {
    const productId = card.getAttribute('data-cart-rec-product-id');
    if (productId) existingProductIds.add(productId);
  }

  try {
    const responses = await Promise.all(
      productIds.map(async (productId) => {
        const params = new URLSearchParams({
          product_id: productId,
          limit: '10',
          section_id: AUTO_SECTION_ID,
          intent: 'related',
        });
        const response = await fetch(`${url}?${params}`);
        if (!response.ok) return null;

        const html = document.createElement('div');
        html.innerHTML = await response.text();
        return html.querySelector(AUTO_CARDS_SELECTOR);
      })
    );

    if (!wrapper.isConnected || wrapper.dataset.autoRecommendationsLoading !== requestKey) return;

    for (const cards of responses) {
      if (!cards) continue;
      for (const card of Array.from(cards.children)) {
        const recommendedProductId = card.getAttribute('data-cart-rec-product-id');
        if (!recommendedProductId || existingProductIds.has(recommendedProductId)) continue;

        existingProductIds.add(recommendedProductId);
        track.append(card);
      }
    }

    if (track.children.length === 0) {
      return;
    }

    wrapper.dataset.autoRecommendationsLoaded = requestKey;
    wrapper.closest('.cart-recommendations')?.removeAttribute('hidden');
    wrapper.dispatchEvent(new CustomEvent('cart-recommendations:loaded', { bubbles: true }));

    const prevBtn = wrapper.querySelector(PREV_SELECTOR);
    const nextBtn = wrapper.querySelector(NEXT_SELECTOR);
    if (prevBtn instanceof HTMLButtonElement && nextBtn instanceof HTMLButtonElement) {
      requestAnimationFrame(() => updateArrows(track, prevBtn, nextBtn));
    }
  } catch (error) {
    console.error('Cart auto recommendations error:', error);
  } finally {
    if (wrapper.dataset.autoRecommendationsLoading === requestKey) {
      delete wrapper.dataset.autoRecommendationsLoading;
    }
  }
}

function loadAllAutoRecommendations(root = document) {
  if (root instanceof HTMLElement && root.matches(CAROUSEL_SELECTOR)) {
    loadAutoRecommendations(root);
  }

  const carousels = root.querySelectorAll?.(CAROUSEL_SELECTOR) || [];
  for (const wrapper of carousels) {
    loadAutoRecommendations(wrapper);
  }
}

function scheduleInit(root = document) {
  const refresh = () => {
    loadAllAutoRecommendations(root);
    initCartRecommendations(root);
  };

  refresh();
  requestAnimationFrame(refresh);
  setTimeout(refresh, 300);
}

window.cartRecQuickAdd = function cartRecQuickAdd(btn, variantId) {
  if (!(btn instanceof HTMLButtonElement) || btn.disabled || !variantId) return;

  const originalText = btn.textContent?.trim() || 'Quick add';
  btn.disabled = true;
  btn.textContent = 'Adding...';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Add failed');
      return res.json();
    })
    .then(() => fetch('/cart.js'))
    .then((res) => res.json())
    .then((cart) => {
      btn.textContent = 'Added';

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-rec-quick-add', {
          itemCount: cart.item_count,
          source: 'cart-rec-quick-add',
          sections: {},
        })
      );

      document.querySelector('cart-drawer-component')?.open?.();
      scheduleInit(document);

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1800);
    })
    .catch(() => {
      btn.textContent = 'Failed';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    });
};

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const prevBtn = target.closest(PREV_SELECTOR);
    const nextBtn = target.closest(NEXT_SELECTOR);
    const arrow = prevBtn || nextBtn;

    if (!(arrow instanceof HTMLButtonElement)) return;

    const wrapper = arrow.closest(CAROUSEL_SELECTOR);
    const track = wrapper?.querySelector(TRACK_SELECTOR);

    if (!(track instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    scrollCarousel(track, prevBtn ? -1 : 1);
  },
  true
);

document.addEventListener('DOMContentLoaded', () => scheduleInit(document));
document.addEventListener(CartUpdateEvent.eventName, () => scheduleInit(document));

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.target instanceof Element) {
      const wrapper = mutation.target.matches(CAROUSEL_SELECTOR)
        ? mutation.target
        : mutation.target.closest(CAROUSEL_SELECTOR);

      if (wrapper) {
        scheduleInit(wrapper);
        return;
      }
    }

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches(CAROUSEL_SELECTOR)) {
        scheduleInit(node.parentElement || document);
        return;
      }

      if (node.querySelector(CAROUSEL_SELECTOR)) {
        scheduleInit(node);
        return;
      }
    }
  }
}).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-auto-product-ids', 'data-cart-product-ids'],
  childList: true,
  subtree: true,
});

scheduleInit(document);
